import React, { useState, useEffect, useCallback } from 'react';
import { fetchCashFlowStatement } from '../api';

export default function CashFlowReport({ authAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(0, 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchCashFlowStatement(authAxios, {
        start_date: startDate, end_date: endDate,
      });
      setData(result);
    } catch (e) {
      console.error('Failed to load cash flow statement', e);
    } finally {
      setLoading(false);
    }
  }, [authAxios, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const fmt = (v) => v != null ? Number(v).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  const Section = ({ title, items, total }) => (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
      <table className="w-full text-sm">
        <tbody>
          {items.map((item, i) => (
            <tr key={i} className="border-b hover:bg-gray-50">
              <td className="px-4 py-1.5">{item.label || item.name}</td>
              <td className="px-4 py-1.5 text-right w-36">{fmt(item.amount)}</td>
            </tr>
          ))}
          <tr className="border-t-2 font-bold bg-gray-50">
            <td className="px-4 py-2">Net Cash from {title}</td>
            <td className="px-4 py-2 text-right">{fmt(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6 space-y-4">
      <h2 className="text-2xl font-bold text-gray-800">Cash Flow Statement</h2>

      <div className="flex gap-4 items-end bg-gray-50 p-4 rounded-lg">
        <div>
          <label className="block text-sm text-gray-600 mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {data && (
        <div className="space-y-2">
          {/* The API returns `period: {start, end}`, not start_date/end_date.
              Six response keys were read under the wrong names, so most of
              this statement rendered blank or `undefined`. [F-4] */}
          <p className="text-sm text-gray-500">
            For the period {data.period?.start} to {data.period?.end}
          </p>

          {/* Net Income */}
          <div className="flex justify-between px-4 py-2 bg-blue-50 rounded font-semibold">
            <span>Net Income</span>
            <span>{fmt(data.net_income)}</span>
          </div>

          <Section title="Operating Activities" items={data.operating_adjustments || []} total={data.cash_from_operations} />
          <Section title="Investing Activities" items={data.investing_activities || []} total={data.cash_from_investing} />
          <Section title="Financing Activities" items={data.financing_activities || []} total={data.cash_from_financing} />

          {/* Summary */}
          <div className="border-t-4 space-y-2 pt-4">
            <div className="flex justify-between px-4 py-1 text-sm">
              <span>Net Increase / (Decrease) in Cash</span>
              <span className="font-bold">{fmt(data.net_change_in_cash)}</span>
            </div>
            <div className="flex justify-between px-4 py-1 text-sm">
              <span>Opening Cash Balance</span>
              <span>{fmt(data.opening_cash)}</span>
            </div>
            <div className="flex justify-between px-4 py-2 font-bold text-lg bg-gray-100 rounded">
              <span>Closing Cash Balance</span>
              <span>{fmt(data.closing_cash)}</span>
            </div>
            <div className="flex justify-end px-4">
              <span className={`px-3 py-1 rounded text-sm font-medium ${data.reconciles ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {data.reconciles ? 'Reconciles' : 'DOES NOT RECONCILE'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
